/**
 * @file        src/modals/PhotoGalleryModal.jsx
 * @description Full hotel photo gallery shown as a centered card: a large hero
 *              image with prev/next arrows on top, a "Hotel Images" heading, and
 *              a scrollable thumbnail grid below (the active thumb is ring-
 *              highlighted; clicking one swaps the hero). Keyboard ←/→/Esc work.
 *
 *              Opened via nh:open-modal with EITHER payload shape:
 *                { groups: [{ label, images:[url] }], title }   ← flattened here
 *                { images: [url], title }
 * @author      Kinetika Labs
 * @created     2026-06-23
 * @changelog
 *   - 2026-08-18 Claude — Icon refresh: the shared glyphs (close, chevrons, check,
 *                          map pin, arrows, search, calendar) redrawn as one
 *                          consistent filled 24×24 family. Path data only — every
 *                          <svg> wrapper, class and size is unchanged.
 *   - 2026-08-07 Claude — Hero photo now fills the card's WIDTH. It was sized by
 *                          height (`max-h-[46vh] w-auto`), so a landscape shot sat
 *                          at roughly 60% of the card with dead panel either side.
 *                          Now `w-full h-auto` — the height follows the image's own
 *                          aspect ratio, so it is scaled and never stretched — with
 *                          a max-h stop for portrait shots, where `object-contain`
 *                          letterboxes instead of distorting.
 *   - 2026-06-23 Claude — Initial single-hero lightbox.
 *   - 2026-06-24 Claude — Centered hero + thumbnail-grid layout matching the
 *                         approved reference (hero w/ arrows, "Hotel Images"
 *                         grid below, active thumb highlighted).
 */
import { useEffect, useMemo, useState } from 'react';

export default function PhotoGalleryModal({ open, onClose, payload }) {
  const title = payload?.title || 'Hotel Images';

  // Accept either { groups } or { images } — flatten + de-dupe to one list.
  const images = useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (u) => { if (u && !seen.has(u)) { seen.add(u); out.push(u); } };
    if (Array.isArray(payload?.groups)) {
      payload.groups.forEach((g) => (g?.images || []).forEach(push));
    }
    (payload?.images || []).forEach(push);
    return out;
  }, [payload]);

  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [payload]);

  const len = images.length;

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowLeft') setIdx((i) => (i - 1 + len) % len);
      if (e.key === 'ArrowRight') setIdx((i) => (i + 1) % len);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, len]);

  if (!open || !len) return null;

  const go = (d) => setIdx((i) => (i + d + len) % len);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-night/80 backdrop-blur-sm p-3 sm:p-6" onClick={onClose}>
      <div
        className="relative w-full max-w-5xl max-h-[92vh] flex flex-col rounded-3xl border border-line bg-panel/95 overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-night/70 border border-ink/10 backdrop-blur flex items-center justify-center text-ink hover:bg-neon hover:text-onaccent cursor-pointer transition"
          aria-label="Close gallery"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="currentColor" d="M18.3 7.1 16.9 5.7 12 10.6 7.1 5.7 5.7 7.1l4.9 4.9-4.9 4.9 1.4 1.4 4.9-4.9 4.9 4.9 1.4-1.4-4.9-4.9z" /></svg>
        </button>

        {/* hero — WIDTH-driven, not height-driven. It used to be
            `max-h-[46vh] w-auto`, so the photo was sized by its height and a
            landscape shot ended up ~60% of the card with dead panel either side.
            `w-full` makes it span the card; `h-auto` lets the height follow the
            image's own aspect ratio, so it is scaled, never stretched. The max-h
            is the safety stop for PORTRAIT shots (full width would run them off
            the screen); when it bites, `object-contain` letterboxes rather than
            distorting. */}
        <div className="relative bg-night/60 flex items-center justify-center shrink-0 p-3 sm:p-5">
          <img src={images[idx]} alt="" className="w-full h-auto max-h-[72vh] object-contain rounded-2xl" />
          {len > 1 && (
            <>
              <button onClick={() => go(-1)} className="absolute left-4 sm:left-7 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-night/60 border border-ink/10 backdrop-blur flex items-center justify-center text-ink hover:bg-neon hover:text-onaccent cursor-pointer transition" aria-label="Previous">
                <svg viewBox="0 0 24 24" className="w-6 h-6"><path fill="currentColor" d="M14.6 18.2 8.4 12l6.2-6.2 1.6 1.6L11.6 12l4.6 4.6z" /></svg>
              </button>
              <button onClick={() => go(1)} className="absolute right-4 sm:right-7 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-night/60 border border-ink/10 backdrop-blur flex items-center justify-center text-ink hover:bg-neon hover:text-onaccent cursor-pointer transition" aria-label="Next">
                <svg viewBox="0 0 24 24" className="w-6 h-6"><path fill="currentColor" d="M9.4 5.8 15.6 12l-6.2 6.2-1.6-1.6L12.4 12 7.8 7.4z" /></svg>
              </button>
              <span className="absolute bottom-4 right-6 px-2.5 py-1 rounded-full bg-night/70 border border-ink/10 text-[11px] font-semibold text-ink">{idx + 1} / {len}</span>
            </>
          )}
        </div>

        {/* thumbnail grid */}
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 sm:px-6 py-4 border-t border-line">
          <p className="font-display font-bold mb-3">{title}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {images.map((g, gi) => (
              <button
                key={gi}
                onClick={() => setIdx(gi)}
                className={`relative rounded-xl overflow-hidden border-2 transition cursor-pointer ${
                  gi === idx ? 'border-neon ring-1 ring-neon' : 'border-transparent hover:border-line'
                }`}
              >
                <img src={g} alt="" loading="lazy" className="w-full h-28 sm:h-32 object-cover" />
                {gi !== idx && <span className="absolute inset-0 bg-night/10 hover:bg-transparent transition" />}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
